import { useEffect } from "react";
import { useAudioVideo } from "amazon-chime-sdk-component-library-react";
import { useShallow } from "zustand/react/shallow";
import { usePlatformStore } from "@platform/store";
import { useDevices } from "../providers/DevicesProvider";
import { isContinuityMicLabel } from "../utils/DeviceManager";
import { suggestedNonContinuityMic } from "../utils/suggestedNonContinuityMic";
import { logMeetingEvent } from "@core/features/videoMeeting/utils/logMeetingEvent";

export type IPhoneMicAlertSurface = "lobby" | "meeting";

// Mobile browsers are gated out — on iPhone/iPad/Android the device's own mic
// is the user's mic by definition. Continuity routing only happens on macOS
// desktop; gating on "is mobile" covers all desktop browsers without coupling
// to OS version strings.
function isMobileBrowser(): boolean {
  if (typeof navigator === "undefined") return false;
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
}

/**
 * Detect when the desktop OS has routed the user's iPhone or iPad as the
 * active microphone via macOS Continuity. When detected, sets
 * `pendingIPhoneMicAlert` on the video slice; UI consumers (IPhoneMicDialog
 * pre-join, useIPhoneMicToast in-meeting) render off that state.
 *
 * Trigger condition (all four must hold):
 * 1. Desktop browser (no-op on mobile)
 * 2. Currently selected audio_input label matches Continuity rule
 * 3. At least one alternative non-Continuity audio input is enumerated
 * 4. User has not explicitly chosen the iPhone in this session
 *
 * Mounts in DevicesProvider so it covers both lobby and in-meeting.
 */
export function useIPhoneMicAlert(): void {
  const { audioInputDevices, selectedAudioInputDevice } = useDevices();
  const audioVideo = useAudioVideo();

  const { pending, userExplicitlyChose, setPending } = usePlatformStore(
    useShallow((s) => ({
      pending: s.pendingIPhoneMicAlert,
      userExplicitlyChose: s.userExplicitlyChoseIPhoneMic,
      setPending: s.setPendingIPhoneMicAlert,
    })),
  );

  useEffect(() => {
    if (isMobileBrowser()) {
      if (pending) setPending(null);
      return;
    }

    const selectedDevice = audioInputDevices.find(
      (d) => d.deviceId === selectedAudioInputDevice,
    );
    const selectedLabel = selectedDevice?.label ?? "";

    if (!isContinuityMicLabel(selectedLabel) || userExplicitlyChose) {
      if (pending) setPending(null);
      return;
    }

    const target = suggestedNonContinuityMic(audioInputDevices);
    if (!target) {
      if (pending) setPending(null);
      return;
    }

    const next = {
      suggestedTargetId: target.deviceId,
      suggestedTargetLabel: target.label,
      currentDeviceLabel: selectedLabel,
    };

    // No-op if pending is already current — prevents re-emitting `shown` on
    // every render of the hook's deps.
    if (
      pending &&
      pending.suggestedTargetId === next.suggestedTargetId &&
      pending.currentDeviceLabel === next.currentDeviceLabel
    ) {
      return;
    }

    setPending(next);
    const surface: IPhoneMicAlertSurface = audioVideo ? "meeting" : "lobby";
    logIPhoneMicAlertEvent("iphone_mic_alert_shown", {
      surface,
      current_device_label: next.currentDeviceLabel,
      suggested_target_label: next.suggestedTargetLabel,
    });
  }, [
    audioInputDevices,
    selectedAudioInputDevice,
    userExplicitlyChose,
    pending,
    setPending,
    audioVideo,
  ]);
}

/**
 * Fire-and-forget event logger for the four iPhone-mic-alert event types.
 * Adds meetingId/roomId/user context from the platform store, matching the
 * `logDeviceEvent` pattern in useDeviceHotPlug.
 */
export function logIPhoneMicAlertEvent(
  eventType:
    | "iphone_mic_alert_shown"
    | "iphone_mic_alert_switched"
    | "iphone_mic_alert_dismissed"
    | "iphone_mic_kept",
  extra: Record<string, unknown>,
): void {
  const s = usePlatformStore.getState();
  logMeetingEvent({
    event_type: eventType,
    meeting_id: s.meetingId,
    room_id: s.roomId,
    user: s.user ? { id: s.user.id } : null,
    ...extra,
  });
}
